/**
 * Czysta logika kreatora wyceny (bez DOM), testowana jednostkowo.
 */

export const CONTACT_EMAIL = "reckontakt@recordsilesia.com";
export const LAST_STEP = 2;

export const SERVICE_LABELS = {
  live: "Transmisja na żywo",
  skrot: "Skrót z golami",
  caly: "Całe nagranie",
  sponsor: "Logo sponsora",
};

export const COUNT_LABELS = {
  1: "1 mecz",
  kilka: "Kilka meczów",
  runda: "Cała runda",
  sezon: "Cały sezon",
};

/**
 * @param {number} n liczba meczów
 * @returns {string} np. „2 mecze”, „5 meczów”, „22 mecze”
 */
export function matchesLabel(n) {
  if (n === 1) return "1 mecz";
  const lastDigit = n % 10;
  const lastTwo = n % 100;
  const few = lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14);
  return `${n} ${few ? "mecze" : "meczów"}`;
}

export const MIN_MATCHES = 2;
export const MAX_MATCHES = 60;

/**
 * Zostawia w polu tylko cyfry (najwyżej dwie).
 * @param {string} value
 * @returns {string}
 */
export function digitsOnly(value) {
  return (value ?? "").replace(/\D/g, "").slice(0, 2);
}

/**
 * Zmienia liczbę meczów przyciskami −/+ w zakresie MIN_MATCHES..MAX_MATCHES.
 * Puste pole traktujemy jak „jeszcze nic”: + daje 2, − też daje 2.
 * @param {string} current wartość pola
 * @param {number} delta -1 albo +1
 * @returns {number}
 */
export function stepMatches(current, delta) {
  const n = Number.parseInt(current, 10);
  const next = Number.isNaN(n) ? MIN_MATCHES : n + delta;
  return Math.min(MAX_MATCHES, Math.max(MIN_MATCHES, next));
}

/**
 * @param {string} choice klucz z COUNT_LABELS
 * @param {string} [howMany] liczba wpisana przy „Kilka meczów”
 * @returns {string} opis liczby meczów do podsumowania
 */
export function countSummary(choice, howMany) {
  const n = Number(howMany);
  if (choice === "kilka" && Number.isInteger(n) && n >= 2) return matchesLabel(n);
  return COUNT_LABELS[choice] ?? "";
}

/**
 * @param {number} step
 * @returns {number} numer kroku ograniczony do zakresu 0..LAST_STEP
 */
export function clampStep(step) {
  return Math.min(LAST_STEP, Math.max(0, step));
}

/**
 * @param {string[]} serviceKeys klucze z SERVICE_LABELS
 * @param {string} matchCount np. "1 mecz"
 * @returns {string}
 */
export function buildSummary(serviceKeys, matchCount) {
  const services = serviceKeys.map((key) => SERVICE_LABELS[key]).filter(Boolean);
  return `${services.join(", ") || "nic nie wybrano"} · ${matchCount}`;
}

/**
 * @param {string} value
 * @returns {string} komunikat błędu albo pusty string
 */
export function validateClub(value) {
  const trimmed = (value ?? "").trim();
  if (trimmed.length === 0) return "Podaj nazwę klubu.";
  if (trimmed.length < 2) return "Nazwa klubu jest za krótka.";
  return "";
}

/**
 * Akceptuje numery z odstępami, myślnikami, nawiasami i prefiksem „+".
 * @param {string} value
 * @returns {string} komunikat błędu albo pusty string
 */
export function validatePhone(value) {
  const trimmed = (value ?? "").trim();
  if (trimmed.length === 0) return "Podaj numer telefonu.";
  if (!/^\+?[\d\s()-]+$/.test(trimmed)) return "Numer może zawierać tylko cyfry.";
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 9 || digits.length > 15) return "Podaj poprawny numer telefonu.";
  return "";
}

/**
 * @param {string} value
 * @returns {string} komunikat błędu albo pusty string
 */
export function validateEmail(value) {
  const trimmed = (value ?? "").trim();
  if (trimmed.length === 0) return "Podaj adres e-mail.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "Podaj poprawny adres e-mail.";
  return "";
}

/**
 * Wymaga nazwy rozgrywek albo linku do klubu (np. z laczynaspilka.pl).
 * @param {string} league nazwa rozgrywek
 * @param {string} link adres strony klubu
 * @returns {string} komunikat błędu albo pusty string
 */
export function validateLeagueOrLink(league, link) {
  const name = (league ?? "").trim();
  const url = (link ?? "").trim();
  if (!name && !url) return "Podaj nazwę rozgrywek albo wklej link do klubu z Łączy nas piłka.";
  if (url && !/^https?:\/\/\S+\.\S+$/i.test(url)) {
    return "Wklej pełny link, zaczynający się od https://";
  }
  return "";
}

/**
 * @param {{klub: string, rozgrywki?: string, link?: string, telefon: string, email: string, zamowienie: string}} quote
 * @param {string} [email] adres, na który trafia zapytanie
 * @returns {string} link mailto: z uzupełnionym tematem i treścią
 */
export function buildQuoteMailto(quote, email = CONTACT_EMAIL) {
  const subject = `Prośba o wycenę – ${quote.klub.trim()}`;
  const body = [
    `Klub: ${quote.klub.trim()}`,
    quote.rozgrywki?.trim() && `Rozgrywki: ${quote.rozgrywki.trim()}`,
    quote.link?.trim() && `Link: ${quote.link.trim()}`,
    `Telefon: ${quote.telefon.trim()}`,
    `E-mail: ${quote.email.trim()}`,
    `Zamówienie: ${quote.zamowienie}`,
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
